import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	InputAdornment,
	Switch,
	TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RefreshIcon from "@material-ui/icons/Refresh";
import React, { useEffect, useState } from "react";
import { alertError, alertSuccess } from "../../../utils/logger";

function getEmptyOperario() {
	return {
		id: null,
		nombre: "",
		apellidos: "",
		operario_categoria_id: null,
		email: "",
		telefono: "",
		coste_hora: 0,
		es_externo: false,
		proveedor_id: null,
		activo: true,
	};
}

const EditOperarioDialog = (props) => {
	const { title, open, setOpen, data, categories, suppliers, onSave, onGenerateMobileAccess } = props;

	const [operario, setOperario] = useState(getEmptyOperario());
	const [mobileAccessUrl, setMobileAccessUrl] = useState("");
	const [generatingMobileAccess, setGeneratingMobileAccess] = useState(false);

	useEffect(() => {
		if (!open) return;

		setOperario({
			...getEmptyOperario(),
			...data,
			operario_categoria_id:
				data?.operario_categoria_id ?? data?.categoria?.id ?? null,
		});
		setMobileAccessUrl("");
	}, [open, data]);

	const handleChange = (element) => (event) => {
		setOperario({ ...operario, [element]: event.target.value });
	};

	const handleSubmit = () => {
		if (!operario?.nombre?.trim()) {
			alertError({ error: "El nombre es obligatorio." });
			return;
		}

		if (!operario?.apellidos?.trim()) {
			alertError({ error: "Los apellidos son obligatorios." });
			return;
		}

		onSave({
			id: operario.id,
			nombre: operario.nombre?.trim(),
			apellidos: operario.apellidos?.trim(),
			operario_categoria_id: operario.operario_categoria_id,
			email: operario.email?.trim() || null,
			telefono: operario.telefono?.trim() || null,
			coste_hora: Math.max(0, Number(operario.coste_hora) || 0),
			es_externo: Boolean(operario.es_externo),
			proveedor_id: operario.es_externo ? operario.proveedor_id || null : null,
			activo: operario.activo ?? true,
		});
	};

	const handleGenerateMobileAccess = async () => {
		if (!operario?.id) return;
		setGeneratingMobileAccess(true);
		try {
			const url = await onGenerateMobileAccess(operario.id);
			setMobileAccessUrl(url);
			setOperario((current) => ({ ...current, tiene_token_acceso_movil: true }));
			alertSuccess({
				title: "Enlace renovado",
				customMessage: "Copia el nuevo acceso móvil antes de cerrar esta ventana.",
			});
		} catch (error) {
			alertError({
				error,
				customMessage: "No se ha podido generar el acceso móvil del operario.",
			});
		} finally {
			setGeneratingMobileAccess(false);
		}
	};

	const handleCopyMobileAccess = async () => {
		if (!mobileAccessUrl) return;
		try {
			await navigator.clipboard.writeText(mobileAccessUrl);
			alertSuccess({ customMessage: "Enlace de acceso copiado." });
		} catch {
			alertError({ error: "No se ha podido copiar el enlace. Selecciónalo manualmente." });
		}
	};

	return (
		<Dialog
			fullWidth
			maxWidth="md"
			open={open}
			onClose={() => setOpen(false)}
			aria-labelledby="operario-dialog"
			className="backdrop"
		>
			<DialogTitle id="operario-dialog">{title}</DialogTitle>
			<DialogContent>
				<div className="row d-flex align-items-center">
					<div className="col-md-6">
						<TextField
							label="Nombre"
							value={operario.nombre}
							onChange={handleChange("nombre")}
							InputLabelProps={{ shrink: true }}
							margin="normal"
							variant="outlined"
							required
							fullWidth
						/>
					</div>
					<div className="col-md-6">
						<TextField
							label="Apellidos"
							value={operario.apellidos}
							onChange={handleChange("apellidos")}
							InputLabelProps={{ shrink: true }}
							margin="normal"
							variant="outlined"
							required
							fullWidth
						/>
					</div>
					<div className="col-md-6">
						<Autocomplete
							id="autocomplete-operario-categoria"
							options={categories || []}
							getOptionLabel={(option) => option?.descripcion || ""}
							value={
								categories?.find(
									(x) => x.id === operario.operario_categoria_id
								) || null
							}
							onChange={(event, selected) => {
								setOperario({
									...operario,
									operario_categoria_id: selected ? selected.id : null,
								});
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Categoría"
									margin="normal"
									variant="outlined"
									InputLabelProps={{ shrink: true }}
								/>
							)}
						/>
					</div>
					<div className="col-md-6">
						<TextField
							label="Email"
							value={operario.email || ""}
							onChange={handleChange("email")}
							InputLabelProps={{ shrink: true }}
							margin="normal"
							variant="outlined"
							fullWidth
						/>
					</div>
					<div className="col-md-6">
						<TextField
							label="Teléfono"
							value={operario.telefono || ""}
							onChange={handleChange("telefono")}
							InputLabelProps={{ shrink: true }}
							margin="normal"
							variant="outlined"
							fullWidth
						/>
					</div>
					<div className="col-md-6">
						<TextField
							label="Coste/hora real (€)"
							type="number"
							value={operario.coste_hora ?? 0}
							onChange={handleChange("coste_hora")}
							inputProps={{ min: 0, step: "0.0001" }}
							InputLabelProps={{ shrink: true }}
							margin="normal"
							variant="outlined"
							fullWidth
						/>
					</div>
					<div className="col-md-6">
						<FormControlLabel
							control={
								<Switch
									checked={Boolean(operario.es_externo)}
									onChange={(event) =>
										setOperario({
											...operario,
											es_externo: event.target.checked,
											proveedor_id: event.target.checked
												? operario.proveedor_id
												: null,
										})
									}
									color="primary"
								/>
							}
							label="Operario externo"
						/>
					</div>
					{operario.es_externo && (
						<div className="col-md-6">
							<Autocomplete
								id="autocomplete-operario-proveedor"
								options={suppliers || []}
								getOptionLabel={(option) => option?.nombre || ""}
								value={
									suppliers?.find(
										(x) => x.id === operario.proveedor_id
									) || null
								}
								onChange={(event, selected) =>
									setOperario({
										...operario,
										proveedor_id: selected?.id || null,
									})
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Proveedor asociado (opcional)"
										margin="normal"
										variant="outlined"
										InputLabelProps={{ shrink: true }}
									/>
								)}
							/>
						</div>
					)}
					{operario.id && (
						<div className="col-12 mt-4">
							<div className="border rounded p-4" style={{ background: "#F7F9FC" }}>
								<div className="d-flex justify-content-between align-items-center mb-2">
									<div className="font-weight-bolder text-dark">Acceso móvil</div>
									<Button
										variant="outlined"
										color="primary"
										onClick={handleGenerateMobileAccess}
										disabled={generatingMobileAccess}
										startIcon={<RefreshIcon />}
									>
										{generatingMobileAccess ? "Generando..." : operario.tiene_token_acceso_movil ? "Renovar enlace" : "Generar enlace"}
									</Button>
								</div>
								<div className="text-muted small mb-3">
									Cada renovación invalida el enlace anterior. Por seguridad, el token solo se muestra al generarlo.
								</div>
								{mobileAccessUrl && (
									<TextField
										label="Enlace de acceso recién generado"
										value={mobileAccessUrl}
										InputLabelProps={{ shrink: true }}
										variant="outlined"
										fullWidth
										InputProps={{
											readOnly: true,
											endAdornment: (
												<InputAdornment position="end">
													<Button color="primary" onClick={handleCopyMobileAccess} startIcon={<FileCopyIcon />}>Copiar</Button>
												</InputAdornment>
											),
										}}
									/>
								)}
							</div>
						</div>
					)}
				</div>
			</DialogContent>
			<DialogActions>
				<Button
					className="mr-2"
					onClick={() => setOpen(false)}
					variant="outlined"
				>
					Cancelar
				</Button>
				<Button onClick={handleSubmit} variant="outlined" color="primary">
					Guardar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EditOperarioDialog;
